import { MMRC_ITEMS } from '../tests/MMRCTest';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import QuestionnaireResultHero from '../components/QuestionnaireResultHero';

export const getMMRCSeverityLabel = (grade?: number | null): string => {
  if (typeof grade !== 'number') return '—';
  if (grade === 0) return 'Dispneia apenas em exercício intenso';
  if (grade === 1) return 'Dispneia leve';
  if (grade === 2) return 'Dispneia moderada';
  return 'Dispneia importante';
};

export const getMMRCSeverityRangeLabel = (grade?: number | null): string => {
  if (typeof grade !== 'number') return 'Classificação indisponível';
  return `Grau ${grade} • ${getMMRCSeverityLabel(grade)}`;
};

export const getMMRCSeverityTone = (grade?: number | null): string => {
  if (typeof grade !== 'number') return 'bg-slate-100 text-slate-700';
  if (grade <= 1) return 'bg-emerald-100 text-emerald-700';
  if (grade === 2) return 'bg-amber-100 text-amber-700';
  return 'bg-rose-100 text-rose-700';
};

export const getMMRCScoreColor = (grade?: number | null): string => {
  if (typeof grade !== 'number') return '#475569';
  if (grade <= 1) return '#10b981';
  if (grade === 2) return '#f59e0b';
  return '#f97316';
};

export const getMMRCInterpretationText = (grade?: number | null): string => {
  if (typeof grade !== 'number') {
    return 'Não há dados suficientes para interpretar a escala mMRC.';
  }

  if (grade === 0) {
    return 'A dispneia está restrita a esforços intensos, sugerindo baixa limitação funcional nas atividades habituais.';
  }

  if (grade === 1) {
    return 'Há dispneia em marcha acelerada ou em aclive leve, com repercussão ainda discreta na rotina.';
  }

  if (grade === 2) {
    return 'A pontuação sugere dispneia moderada, com redução do ritmo de marcha ou necessidade de pausas durante o caminhar habitual.';
  }

  if (grade === 3) {
    return 'A dispneia já impõe limitação importante, com pausas frequentes após pequena distância no plano.';
  }

  return 'A pontuação sugere dispneia muito importante, com limitação severa para sair de casa ou até para atividades como vestir-se.';
};

interface MMRCResultRendererProps {
  payload: {
    grade?: unknown;
    description?: unknown;
  } | null | undefined;
}

export default function MMRCResultRenderer({ payload }: MMRCResultRendererProps) {
  const grade = typeof payload?.grade === 'number' ? Math.max(0, Math.min(4, payload.grade)) : null;
  const selectedDescription =
    typeof payload?.description === 'string' && payload.description.trim().length > 0
      ? payload.description.trim()
      : MMRC_ITEMS.find((item) => item.grade === grade)?.text ?? 'Descrição não informada.';
  const severity = getMMRCSeverityRangeLabel(grade);
  const scoreColor = getMMRCScoreColor(grade);
  const interpretation = resolveAssessmentInterpretationText(payload, getMMRCInterpretationText(grade));

  return (
    <div className="space-y-6">
      <QuestionnaireResultHero
        title="Escala mMRC"
        value={grade ?? '—'}
        valueColor={scoreColor}
        valueCaption={typeof grade === 'number' ? `grau ${grade} de 4` : 'Grau indisponível'}
        classification={severity}
        interpretation={
          <>
            <p>{interpretation}</p>
            <p className="mt-3 font-medium text-slate-700">{selectedDescription}</p>
          </>
        }
      >
          <div className="space-y-2">
            <div className="relative py-1">
              <div
                className="h-9 rounded-full"
                style={{
                  background:
                    'linear-gradient(90deg, #1cc8a0 0%, #9cd26e 24%, #f2d61a 50%, #ff9e44 74%, #f43f5e 100%)',
                }}
              />
              {typeof grade === 'number' && (
                <div
                  className="absolute -top-2 h-[calc(100%+16px)] w-1 rounded-full bg-slate-800"
                  style={{ left: `calc(${(Math.max(0, Math.min(4, grade)) / 4) * 100}% - 2px)` }}
                />
              )}
            </div>
            <div className="flex items-center justify-between text-xs font-medium text-slate-500">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
            </div>
          </div>
      </QuestionnaireResultHero>

      <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <th className="px-6 py-4">Grau</th>
              <th className="px-6 py-4">Descrição</th>
              <th className="px-6 py-4">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {MMRC_ITEMS.map((item) => {
              const selected = item.grade === grade;
              return (
                <tr key={item.grade} className={selected ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}>
                  <td className="px-6 py-4 font-black text-slate-900">{item.grade}</td>
                  <td className="px-6 py-4 font-medium text-slate-700">{item.text}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex rounded-lg px-2.5 py-1 text-[10px] font-black uppercase ${
                        selected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {selected ? 'Selecionado' : 'Não selecionado'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
